import { ApiConflictError, ApiError, apiCall } from '@/lib/api-client';

/**
 * Admin API client. The admin surface is separate from the regular user
 * auth surface: it has its own cookie (``regara_admin``), its own
 * session TTL, and its own /api/admin/* router on the backend.
 *
 * Routes:
 *   - GET  /api/admin/auth/config          → sign-in mode + Google client id
 *   - POST /api/admin/auth/google          → Google credential sign-in
 *   - POST /api/admin/auth/dev             → local developer email sign-in
 *   - POST /api/admin/auth/breakglass/*    → emergency password + emailed code
 *   - POST /api/admin/logout               → clear admin cookie
 *   - GET  /api/admin/me                   → 200 if signed in, 401 otherwise
 *   - GET  /api/admin/sessions             → signed-in staff (full admins only)
 *   - POST /api/admin/sessions/revoke-user → end every session one person has
 *   - POST /api/admin/invite               → create invite + send email
 *
 * Used only by the chrome-less /admin/* React routes — never imported
 * by customer-facing pages.
 */

export type AdminAuthConfig = {
  mode: 'google' | 'dev';
  google_client_id: string | null;
  breakglass_enabled: boolean;
};

/** `role: 'admin'` — full write access (ADMIN_EMAILS). `role: 'viewer'` — any
 *  other verified @regara.ai account: reads every admin page but the server
 *  refuses every write (403), so the UI disables the controls that would hit one. */
export type AdminMe = { email: string; method: string; role: 'admin' | 'viewer' };

export interface InviteArgs {
  email: string;
  /** AI data-use track assigned to this company at invite. Default non_training;
   * training only when the admin (per the boss's call) elects it. */
  ai_training_track: 'non_training' | 'training';
}

export interface InviteResult {
  message: string;
  email: string;
  signup_link: string;
  expires_at: string;
}

export async function getAdminAuthConfig(): Promise<AdminAuthConfig> {
  return apiCall<AdminAuthConfig>('/api/admin/auth/config', {
    method: 'GET',
    skipAuthRedirect: true,
  });
}

// All four sign-in calls pass `skipAuthRedirect: true`: a 401 here means "not an
// admin" / "wrong code", not "your session expired" — without it the shared
// api-client's 401 interceptor would navigate the tab to the CUSTOMER /login
// page instead of letting AdminLoginPage show its own error copy.

export async function adminGoogleSignIn(credential: string): Promise<{ message: string }> {
  return apiCall<{ message: string }>('/api/admin/auth/google', {
    method: 'POST',
    json: { credential },
    skipAuthRedirect: true,
  });
}

export async function adminDevSignIn(email: string): Promise<{ message: string }> {
  return apiCall<{ message: string }>('/api/admin/auth/dev', {
    method: 'POST',
    json: { email },
    skipAuthRedirect: true,
  });
}

export async function adminBreakglassStart(args: {
  email: string;
  password: string;
}): Promise<{ message: string }> {
  return apiCall<{ message: string }>('/api/admin/auth/breakglass/start', {
    method: 'POST',
    json: args,
    skipAuthRedirect: true,
  });
}

export async function adminBreakglassVerify(args: {
  email: string;
  code: string;
}): Promise<{ message: string }> {
  return apiCall<{ message: string }>('/api/admin/auth/breakglass/verify', {
    method: 'POST',
    json: args,
    skipAuthRedirect: true,
  });
}

export async function adminLogout(): Promise<{ message: string }> {
  return apiCall<{ message: string }>('/api/admin/logout', {
    method: 'POST',
  });
}

export async function adminMe(): Promise<AdminMe> {
  return apiCall<AdminMe>('/api/admin/me', {
    method: 'GET',
    skipAuthRedirect: true,
  });
}

// ── Signed-in staff (offboarding) ───────────────────────────────────────────

export type StaffSessionStatus = 'active' | 'signed_out' | 'expired';

/** One person with an admin-console session in the last 8 hours. `method`, `ip` and
 *  `last_seen_at` describe their most recent live session (or most recent at all). */
export type StaffSession = {
  email: string;
  role: 'admin' | 'viewer';
  status: StaffSessionStatus;
  method: string;
  last_seen_at: string;
  ip: string | null;
  active_sessions: number;
  is_you: boolean;
};

export async function getStaffSessions(): Promise<StaffSession[]> {
  const res = await apiCall<{ sessions: StaffSession[] }>('/api/admin/sessions', { method: 'GET' });
  return res.sessions;
}

export async function signOutStaffMember(email: string): Promise<{ revoked: number }> {
  return apiCall<{ revoked: number }>('/api/admin/sessions/revoke-user', {
    method: 'POST',
    json: { email },
  });
}

export async function adminInvite(args: InviteArgs): Promise<InviteResult> {
  return apiCall<InviteResult>('/api/admin/invite', {
    method: 'POST',
    json: args,
  });
}

// ── Companies / AI data-use track ──────────────────────────────────────────

export interface AdminCompany {
  id: number;
  company: string;
  ai_training_track: 'non_training' | 'training';
  users: number;
  founder_email: string | null;
  status: string;
  /** ≥1 signed-up user hasn't accepted the company's current track yet (e.g.
   *  right after a switch): the track is assigned but not confirmed. */
  pending_reconsent: boolean;
  /** Durable access gate for the whole account; "suspended" blocks every user. */
  access_status: 'active' | 'suspended';
}

export interface AdminCompaniesPage {
  companies: AdminCompany[];
  next_cursor: string | null;
}

/** One keyset page of companies. Pass the previous page's next_cursor to advance. */
export async function getAdminCompanies(
  cursor?: string | null,
  limit = 20,
  search?: string,
): Promise<AdminCompaniesPage> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  if (search && search.trim()) params.set('search', search.trim());
  return apiCall<AdminCompaniesPage>(`/api/admin/companies?${params.toString()}`, {
    method: 'GET',
  });
}

/** Flip a company's AI data-use track. The company is re-gated and must re-accept
 *  the corresponding data-use agreement; prior acceptance is preserved. */
export async function setCompanyTrack(
  id: number,
  ai_training_track: 'non_training' | 'training',
): Promise<AdminCompany> {
  return apiCall<AdminCompany>(`/api/admin/companies/${id}/track`, {
    method: 'POST',
    json: { ai_training_track },
  });
}

export interface AdminAccessResult {
  id: number;
  access_status: 'active' | 'suspended';
}

/** Suspend or restore a whole company's access. Suspending blocks every user
 *  under the account (login + every request) until restored; data is untouched. */
export async function setCompanyAccess(
  id: number,
  status: 'active' | 'suspended',
): Promise<AdminAccessResult> {
  return apiCall<AdminAccessResult>(`/api/admin/companies/${id}/access`, {
    method: 'POST',
    json: { status },
  });
}

/** Suspend or restore a single user's access, independent of their company. */
export async function setUserAccess(
  userId: number,
  status: 'active' | 'suspended',
): Promise<AdminAccessResult> {
  return apiCall<AdminAccessResult>(`/api/admin/users/${userId}/access`, {
    method: 'POST',
    json: { status },
  });
}

// ── Company detail + per-user actions ──────────────────────────────────────

export interface AdminCompanyUser {
  id: number;
  email: string;
  name: string | null;
  roles: string[];
  status: 'active' | 'pending';
  is_founder: boolean;
  created_at: string;
  expires_at: string | null;
  /** Per-user access gate ("suspended" blocks this user even if the company is active). */
  access_status: 'active' | 'suspended';
}

export interface AdminCompanyDetail extends AdminCompany {
  members: AdminCompanyUser[];
}

/** One company + its members (founder + teammates, signed-up or pending). */
export async function getAdminCompanyDetail(id: number): Promise<AdminCompanyDetail> {
  return apiCall<AdminCompanyDetail>(`/api/admin/companies/${id}`, { method: 'GET' });
}

/** Revoke all of a user's active sessions (token_version bump). Reversible. */
export async function forceLogoutUser(userId: number): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/admin/users/${userId}/force-logout`, {
    method: 'POST',
  });
}

/** Resend a pending invite — rotates the access code, returns the fresh link. */
export async function resendUserInvite(userId: number): Promise<InviteResult> {
  return apiCall<InviteResult>(`/api/admin/users/${userId}/resend-invite`, {
    method: 'POST',
  });
}

// ── Pending founder invites ────────────────────────────────────────────────

export interface AdminInvite {
  id: number;
  email: string;
  ai_training_track: 'non_training' | 'training';
  expires_at: string | null;
  created_at: string;
}

export async function getAdminInvites(): Promise<AdminInvite[]> {
  const res = await apiCall<{ invites: AdminInvite[] }>('/api/admin/invites', {
    method: 'GET',
  });
  return res.invites;
}

/** Revoke a pending founder invite (hard-delete; pending-only). */
export async function revokeAdminInvite(id: number): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/admin/invites/${id}`, { method: 'DELETE' });
}

// ── Dossier ops: triage in-flight + recently-failed runs ────────────────────

export interface AdminDossierRun {
  id: number;
  status: string;
  agent_kind: string;
  country: string | null;
  submission_type: string | null;
  product_id: number;
  product_name: string | null;
  company_id: number | null;
  company_name: string | null;
  dispatched_by: string | null;
  created_at: string;
  started_at: string | null;
  heartbeat_at: string | null;
  resume_count: number;
  resume_limit: number;
  /** A saved checkpoint exists — the run can be Continued (force-resumed). */
  has_checkpoint: boolean;
  error_message: string | null;
  age_minutes: number | null;
  /** A running run whose heartbeat is stale (process likely died) — eligible for Continue. */
  stale: boolean;
  /** The customer-facing Run # (an EU / NI dispatch's parts share it); null on legacy rows. */
  run_number: number | null;
  dispatch_group_id: string | null;
  /** Which part of an EU / NI dispatch ("TD", "PMS", "SSCP", "SSP"); null for every other run. */
  part_label: string | null;
}

export async function getAdminDossierRuns(): Promise<AdminDossierRun[]> {
  const res = await apiCall<{ runs: AdminDossierRun[] }>('/api/admin/dossier-runs', {
    method: 'GET',
  });
  return res.runs;
}

/** The dossier concurrency dial: what the admission doorman enforces now. */
export interface AdminDossierConcurrency {
  cap: number; // effective cap
  override: number | null; // runtime dial; null = using the env default
  env_default: number;
  running: number; // current global running-dossier count
}

export async function getDossierConcurrency(): Promise<AdminDossierConcurrency> {
  return apiCall<AdminDossierConcurrency>('/api/admin/dossier-runs/concurrency', {
    method: 'GET',
  });
}

/** Set the runtime concurrency cap (a positive integer), or null to clear the
 *  override and revert to the env default. Takes effect immediately — the
 *  doorman resolves the dial per admission attempt, no redeploy. */
export async function setDossierConcurrency(
  cap: number | null,
): Promise<AdminDossierConcurrency> {
  return apiCall<AdminDossierConcurrency>('/api/admin/dossier-runs/concurrency', {
    method: 'PUT',
    json: { cap },
  });
}

/** The predicate concurrency dial: the cap ONE worker throttles to right now. */
export interface AdminPredicateConcurrency {
  cap: number; // effective cap
  override: number | null; // runtime dial; null = using the env default
  env_default: number;
  running: number; // current global running-predicate count
}

export async function getPredicateConcurrency(): Promise<AdminPredicateConcurrency> {
  return apiCall<AdminPredicateConcurrency>('/api/admin/predicate-runs/concurrency', {
    method: 'GET',
  });
}

/** Set the runtime predicate concurrency cap (a positive integer), or null to clear
 *  the override. Takes effect immediately — the worker re-reads it each loop, no
 *  redeploy. */
export async function setPredicateConcurrency(
  cap: number | null,
): Promise<AdminPredicateConcurrency> {
  return apiCall<AdminPredicateConcurrency>('/api/admin/predicate-runs/concurrency', {
    method: 'PUT',
    json: { cap },
  });
}

export interface AdminAppSettings {
  billing_enabled: boolean; // effective — the master Stripe billing switch
  billing_enabled_override: boolean | null; // runtime dial; null = never set (off)
  billing_enabled_default: boolean;
  billing_provisioned_tenants: number; // >0 means the switch cannot be turned off
  extraction_enabled: boolean; // effective — whether tech-spec LLM extraction runs
  extraction_enabled_override: boolean | null; // runtime dial; null = env default
  extraction_enabled_default: boolean;
  docling_ocr_enabled: boolean; // effective — whether docling runs OCR (scanned/image-heavy)
  docling_ocr_override: boolean | null; // runtime dial; null = env default
  docling_ocr_default: boolean;
  figures_enabled: boolean; // effective — whether the docling figure/vision pass runs
  figures_enabled_override: boolean | null; // runtime dial; null = env default
  figures_enabled_default: boolean;
  // Supporting-document conversion dials (separate from the tech-spec ones).
  supporting_docs_ocr_enabled: boolean;
  supporting_docs_ocr_override: boolean | null;
  supporting_docs_ocr_default: boolean;
  supporting_docs_figures_enabled: boolean;
  supporting_docs_figures_override: boolean | null;
  supporting_docs_figures_default: boolean;
  upload_max_bytes: number; // effective tech-spec upload cap (bytes)
  upload_max_bytes_override: number | null;
  upload_max_bytes_default: number;
  upload_max_bytes_ceiling: number; // safe max an admin can dial to (bytes)
  upload_max_bytes_direct: number; // effective direct-S3 upload cap (bytes, up to 1 GiB)
  upload_max_bytes_direct_override: number | null;
  upload_max_bytes_direct_default: number;
  upload_max_bytes_direct_ceiling: number; // safe max for the S3 path (bytes)
  extraction_concurrency: number; // effective max concurrent extraction builds
  extraction_concurrency_override: number | null;
  extraction_concurrency_default: number;
  extraction_concurrency_ceiling: number; // safe max an admin can dial to
  // Per-product supporting-document ceilings. Both apply, and the same two numbers
  // bound a single upload request — so what the uploader shows is what is enforced.
  supporting_docs_max_files: number;
  supporting_docs_max_files_override: number | null;
  supporting_docs_max_files_default: number;
  supporting_docs_max_files_ceiling: number;
  /** Tech-spec extraction page cap (live dial). */
  extraction_max_pages: number;
  extraction_max_pages_override: number | null;
  extraction_max_pages_default: number;
  extraction_max_pages_ceiling: number;
  /** Tech-spec build time budget in minutes (live dial). */
  extraction_build_timeout_minutes: number;
  extraction_build_timeout_override: number | null;
  extraction_build_timeout_default: number;
  extraction_build_timeout_ceiling: number;
  // Supporting documents: their own page cap and reading time limit.
  supporting_docs_max_pages: number;
  supporting_docs_max_pages_override: number | null;
  supporting_docs_max_pages_default: number;
  supporting_docs_max_pages_ceiling: number;
  supporting_docs_convert_timeout_minutes: number;
  supporting_docs_convert_timeout_override: number | null;
  supporting_docs_convert_timeout_default: number;
  supporting_docs_convert_timeout_ceiling: number;
  supporting_docs_max_bytes: number;
  supporting_docs_max_bytes_override: number | null;
  supporting_docs_max_bytes_default: number;
  supporting_docs_max_bytes_ceiling: number;
  supporting_docs_max_file_bytes: number;
  supporting_docs_max_file_bytes_override: number | null;
  supporting_docs_max_file_bytes_default: number;
  // What the heaviest product already holds — shown beside each limit so lowering one
  // below these numbers is a visible decision, not a surprise.
  supporting_docs_peak_files: number;
  supporting_docs_peak_bytes: number;
  supporting_docs_peak_file_bytes: number;
  // Dossier dispatch — the WHOLE submission across every market, not one file.
  dossier_upload_total_max_bytes: number;
  dossier_upload_total_override: number | null;
  dossier_upload_total_default: number;
  dossier_upload_total_ceiling: number;
  dossier_upload_total_floor: number;
  // Dossier dispatch page — largest clinical document upload (protocol / study report).
  dossier_upload_max_bytes: number;
  dossier_upload_max_bytes_override: number | null;
  dossier_upload_max_bytes_default: number;
  dossier_upload_max_bytes_ceiling: number;
  supporting_docs_max_file_bytes_ceiling: number;
  // Vault uploads (folder upload / bulk file pick) — the presigned browser→S3 path.
  vault_upload_max_files: number;
  vault_upload_max_files_override: number | null;
  vault_upload_max_files_default: number;
  vault_upload_max_files_ceiling: number;
  vault_upload_max_file_bytes: number;
  vault_upload_max_file_bytes_override: number | null;
  vault_upload_max_file_bytes_default: number;
  vault_upload_max_file_bytes_ceiling: number;
  // Target markets — which countries the customer app offers, per product type
  // (company account / product creation / all five dispatch pickers).
  // market_catalog is the full registry-enabled set the checkboxes can offer;
  // each *_countries is the effective list, its *_override null while the dial
  // was never touched (= all).
  market_catalog: { code: string; label: string }[];
  /** Countries the admin can never uncheck (always offered for every type). */
  market_mandatory: string[];
  /** Product types the admin can never switch off (device). */
  type_mandatory: string[];
  markets_device_enabled: boolean;
  markets_device_enabled_override: boolean | null;
  markets_device_countries: string[];
  markets_device_countries_override: string[] | null;
  markets_drug_enabled: boolean;
  markets_drug_enabled_override: boolean | null;
  markets_drug_countries: string[];
  markets_drug_countries_override: string[] | null;
  markets_biologic_enabled: boolean;
  markets_biologic_enabled_override: boolean | null;
  markets_biologic_countries: string[];
  markets_biologic_countries_override: string[] | null;
  // Simulators feature switch: off greys the customer nav tab and refuses new
  // simulator dispatches (existing runs stay readable).
  simulators_enabled: boolean;
  simulators_enabled_override: boolean | null;
  simulators_enabled_default: boolean;
}

/** Partial update — send only the dial(s) you're changing. */
export interface AdminAppSettingsUpdate {
  billing_enabled?: boolean;
  extraction_enabled?: boolean;
  docling_ocr_enabled?: boolean;
  figures_enabled?: boolean;
  supporting_docs_ocr_enabled?: boolean;
  supporting_docs_figures_enabled?: boolean;
  dossier_upload_max_bytes?: number;
  dossier_upload_total_max_bytes?: number;
  vault_upload_max_files?: number;
  vault_upload_max_file_bytes?: number;
  /** Acknowledge that this value strands products that already hold more. */
  confirm_over_limit?: boolean;
  upload_max_bytes?: number;
  upload_max_bytes_direct?: number;
  extraction_concurrency?: number;
  supporting_docs_max_files?: number;
  extraction_max_pages?: number;
  extraction_build_timeout_minutes?: number;
  supporting_docs_max_pages?: number;
  supporting_docs_convert_timeout_minutes?: number;
  supporting_docs_max_bytes?: number;
  supporting_docs_max_file_bytes?: number;
  markets_device_enabled?: boolean;
  markets_device_countries?: string[];
  markets_drug_enabled?: boolean;
  markets_drug_countries?: string[];
  markets_biologic_enabled?: boolean;
  markets_biologic_countries?: string[];
  simulators_enabled?: boolean;
}

export async function getAppSettings(): Promise<AdminAppSettings> {
  return apiCall<AdminAppSettings>('/api/admin/settings', { method: 'GET' });
}

/** Set the runtime tech-spec dials (extraction on/off, upload cap in bytes). Takes
 *  effect immediately — read fresh per request, no redeploy. */
export async function updateAppSettings(
  update: AdminAppSettingsUpdate,
): Promise<AdminAppSettings> {
  return apiCall<AdminAppSettings>('/api/admin/settings', {
    method: 'PUT',
    json: update,
  });
}

/** Size + rough-cost distribution of extracted tech specs — evidence for setting
 *  an extraction cap. Null fields mean no measured extractions yet. */
export interface AdminExtractionStats {
  sample_count: number;
  max_page_count: number | null;
  median_page_count: number | null;
  max_cost_usd: number | null;
  median_cost_usd: number | null;
}

export async function getExtractionStats(): Promise<AdminExtractionStats> {
  return apiCall<AdminExtractionStats>('/api/admin/extraction-stats', { method: 'GET' });
}

/** Force a queued/running dossier run to failed (atomic; clears a wedged run). */
export async function forceFailDossierRun(runId: number): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/admin/dossier-runs/${runId}/force-fail`, {
    method: 'POST',
  });
}

/** Continue a stuck-running run — re-dispatches it IMMEDIATELY from its checkpoint
 *  (not via the sweeper). Requires a saved checkpoint + a stale heartbeat (409 otherwise). */
export async function continueDossierRun(runId: number): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/admin/dossier-runs/${runId}/continue`, {
    method: 'POST',
  });
}

/** Retry a FAILED run — re-queues it for a fresh attempt (fresh retry budgets,
 *  cleared error/output) and re-dispatches it. The run re-runs from scratch (its
 *  checkpoint was wiped on failure). 404 if the run isn't in a failed state. */
export async function retryDossierRun(runId: number): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/admin/dossier-runs/${runId}/retry`, {
    method: 'POST',
  });
}

/** One predicate run for the operator ops panel. Simpler than the dossier row —
 *  predicate has no checkpoint / heartbeat / resume, so actions are Kill + Retry. */
export interface AdminPredicateRun {
  id: number;
  status: string;
  agent_kind: string;
  country: string | null;
  submission_type: string | null;
  product_id: number | null;
  product_name: string | null;
  company_name: string | null;
  dispatched_by: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  agent_retry_count: number;
  selected_k_number: string | null;
  age_minutes: number | null;
  /** A running run past the 30-min zombie threshold (process likely died). */
  stale: boolean;
}

export async function getAdminPredicateRuns(): Promise<AdminPredicateRun[]> {
  const res = await apiCall<{ runs: AdminPredicateRun[] }>('/api/admin/predicate-runs', {
    method: 'GET',
  });
  return res.runs;
}

/** Force a queued/running predicate run to failed (atomic; clears a wedged run). */
export async function forceFailPredicateRun(runId: number): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/admin/predicate-runs/${runId}/force-fail`, {
    method: 'POST',
  });
}

/** Retry a FAILED predicate run — re-queues it for a fresh full re-run and
 *  re-dispatches it (the worker rebuilds every input from the DB by run_id). 404 if
 *  the run isn't in a failed state. */
export async function retryPredicateRun(runId: number): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/admin/predicate-runs/${runId}/retry`, {
    method: 'POST',
  });
}

// ── Archive-import ops: the dedicated SQS worker's concurrency dial + runs triage ─

/** The archive-import per-worker concurrency dial: imports ONE worker handles at once. */
export interface AdminArchiveImportConcurrency {
  cap: number; // effective cap
  override: number | null; // runtime dial; null = using the env default
  env_default: number;
  running: number; // current global 'importing' count
}

export async function getArchiveImportConcurrency(): Promise<AdminArchiveImportConcurrency> {
  return apiCall<AdminArchiveImportConcurrency>('/api/admin/archive-import-runs/concurrency', {
    method: 'GET',
  });
}

/** Set the runtime archive-import concurrency cap (positive integer), or null to clear the
 *  override. Takes effect immediately — the worker re-reads it each loop, no redeploy. */
export async function setArchiveImportConcurrency(
  cap: number | null,
): Promise<AdminArchiveImportConcurrency> {
  return apiCall<AdminArchiveImportConcurrency>('/api/admin/archive-import-runs/concurrency', {
    method: 'PUT',
    json: { cap },
  });
}

/** One archive-import job for the ops panel — enriched for triage. */
export interface AdminArchiveImportRun {
  id: number;
  status: string;
  filename: string;
  company_name: string | null;
  dispatched_by: string | null;
  files_created: number;
  folders_touched: number;
  skipped_count: number;
  import_attempts: number;
  error: string | null;
  created_at: string;
  import_started_at: string | null;
  finished_at: string | null;
  age_minutes: number | null;
  /** An 'importing' job whose claim has been silent past the stale threshold (worker likely died). */
  stale: boolean;
}

export interface AdminArchiveImportRuns {
  runs: AdminArchiveImportRun[];
  /** Approximate DLQ depth; null when the DLQ isn't configured (background mode / tests). */
  dlq_depth: number | null;
}

export async function getAdminArchiveImportRuns(): Promise<AdminArchiveImportRuns> {
  return apiCall<AdminArchiveImportRuns>('/api/admin/archive-import-runs', { method: 'GET' });
}

/** Force a pending/importing archive import to failed (atomic; clears a wedged job). */
export async function forceFailArchiveImport(importId: number): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/admin/archive-import-runs/${importId}/force-fail`, {
    method: 'POST',
  });
}

/** Retry a FAILED archive import — re-queues it and re-dispatches (the worker rebuilds the archive
 *  from storage_key). 404 if it isn't in a failed state. */
export async function retryArchiveImport(importId: number): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/admin/archive-import-runs/${importId}/retry`, {
    method: 'POST',
  });
}

// ── Document extraction ops: triage in-flight + recently-failed extractions ─

/** One tech-spec extraction for the operator ops panel. Extraction has no
 *  checkpoint/resume and no Kill — the only row action is Retry a failed one. */
export interface AdminExtractionRun {
  id: number;
  status: string;
  source_filename: string | null;
  product_id: number;
  product_name: string | null;
  company_name: string | null;
  uploaded_by: string | null;
  created_at: string;
  build_started_at: string | null;
  build_attempts: number;
  /** Why a failed row failed — a fixed phrase per failure class; null while pending. */
  failure_reason: string | null;
  extraction_incomplete: boolean;
  extraction_truncated: boolean;
  page_count: number | null;
  age_minutes: number | null;
  /** A pending build whose claim has been silent past the 30-min stale threshold
   *  (worker likely died; recovery will re-dispatch it). */
  stale: boolean;
}

/** The Document Extraction Runs payload: rows + DLQ depth (null when the DLQ isn't
 *  configured — background dispatch mode / tests). No aggregate health block: the
 *  endpoint is polled and health runs full table scans, so it's kept off the hot
 *  path (the panel doesn't render it). */
export interface AdminExtractionRuns {
  runs: AdminExtractionRun[];
  dlq_depth: number | null;
}

export async function getAdminExtractionRuns(): Promise<AdminExtractionRuns> {
  return apiCall<AdminExtractionRuns>('/api/admin/extraction-runs', { method: 'GET' });
}

/** Retry a FAILED extraction — resets it to pending, clears the build claim, and
 *  re-dispatches the build. 404 if it isn't in a retryable failed state. */
export async function retryExtractionProposal(proposalId: number): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/admin/extraction-proposals/${proposalId}/retry`, {
    method: 'POST',
  });
}

// ── Supporting-document conversions: failed + pending, with the product's quota ─

/** One supporting document for the ops panel — a failed conversion, or one still pending
 *  (`stale` once past the reading time limit) — plus its product's quota usage. */
export interface AdminSupportingDocConversion {
  id: number;
  filename: string;
  status: 'pending' | 'failed';
  /** Why it failed, e.g. "No readable text"; null while pending. */
  failure_reason: string | null;
  size_bytes: number;
  product_id: number;
  product_name: string | null;
  company_name: string | null;
  uploaded_by: string | null;
  uploaded_at: string;
  age_minutes: number;
  stale: boolean;
  used_files: number;
  used_bytes: number;
  max_files: number;
  max_bytes: number;
}

export interface AdminSupportingDocConversions {
  /** The newest rows only (capped server-side); use the totals for counts. */
  documents: AdminSupportingDocConversion[];
  total_failed: number;
  total_pending: number;
}

export async function getAdminSupportingDocConversions(): Promise<AdminSupportingDocConversions> {
  return apiCall<AdminSupportingDocConversions>('/api/admin/supporting-doc-conversions', {
    method: 'GET',
  });
}

/** Retry a FAILED supporting-document conversion. 404 if it isn't failed; 409 if the
 *  product was archived. */
export async function retrySupportingDocConversion(docId: number): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/admin/supporting-docs/${docId}/retry`, {
    method: 'POST',
  });
}

/** The strategy concurrency dial: the cap the admission doorman enforces right now. */
export interface AdminStrategyConcurrency {
  cap: number; // effective cap
  override: number | null; // runtime dial; null = using the env default
  env_default: number;
  running: number; // current global running-strategy count
}

export async function getStrategyConcurrency(): Promise<AdminStrategyConcurrency> {
  return apiCall<AdminStrategyConcurrency>('/api/admin/strategy-runs/concurrency', {
    method: 'GET',
  });
}

/** Set the runtime strategy concurrency cap (a positive integer), or null to clear
 *  the override. Takes effect immediately — the doorman resolves it per attempt and
 *  the worker per pull loop, no redeploy. Strategy only; protocol/report keep the
 *  env default. */
export async function setStrategyConcurrency(
  cap: number | null,
): Promise<AdminStrategyConcurrency> {
  return apiCall<AdminStrategyConcurrency>('/api/admin/strategy-runs/concurrency', {
    method: 'PUT',
    json: { cap },
  });
}

/** One strategy run for the operator ops panel. Mirrors the predicate row —
 *  strategy has no checkpoint/resume, so actions are Kill + Retry. */
export interface AdminStrategyRun {
  id: number;
  status: string;
  agent_kind: string;
  country: string | null;
  product_id: number | null;
  product_name: string | null;
  company_name: string | null;
  dispatched_by: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  agent_retry_count: number;
  recommended_pathway: string | null;
  age_minutes: number | null;
  /** A running run whose heartbeat went stale (10+ min — worker likely died). */
  stale: boolean;
}

export async function getAdminStrategyRuns(): Promise<AdminStrategyRun[]> {
  const res = await apiCall<{ runs: AdminStrategyRun[] }>('/api/admin/strategy-runs', {
    method: 'GET',
  });
  return res.runs;
}

/** Force a queued/running strategy run to failed (atomic; clears a wedged run). */
export async function forceFailStrategyRun(runId: number): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/admin/strategy-runs/${runId}/force-fail`, {
    method: 'POST',
  });
}

/** Retry a FAILED strategy run — re-queues it for a fresh full re-run and
 *  re-dispatches it (the worker rebuilds every input from the DB by run_id). 404 if
 *  the run isn't in a failed state. */
export async function retryStrategyRun(runId: number): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/admin/strategy-runs/${runId}/retry`, {
    method: 'POST',
  });
}

/** The two clinical agents share one admin API shape — their run models are
 *  column-identical twins. `kind` picks the endpoint family. */
export type ClinicalAgentKind = 'clinical-protocol' | 'clinical-report';

/** A clinical agent's concurrency dial: the cap the doorman enforces right now. */
export interface AdminClinicalConcurrency {
  cap: number; // effective cap
  override: number | null; // runtime dial; null = using the env default
  env_default: number;
  running: number; // current global running count for this kind
}

export async function getClinicalConcurrency(
  kind: ClinicalAgentKind,
): Promise<AdminClinicalConcurrency> {
  return apiCall<AdminClinicalConcurrency>(`/api/admin/${kind}-runs/concurrency`, {
    method: 'GET',
  });
}

/** Set the runtime cap for a clinical agent (a positive integer), or null to
 *  clear the override. Takes effect immediately — the doorman resolves it per
 *  attempt and the worker per pull loop, no redeploy. */
export async function setClinicalConcurrency(
  kind: ClinicalAgentKind,
  cap: number | null,
): Promise<AdminClinicalConcurrency> {
  return apiCall<AdminClinicalConcurrency>(`/api/admin/${kind}-runs/concurrency`, {
    method: 'PUT',
    json: { cap },
  });
}

/** One clinical-protocol or clinical-report run for the operator ops panel.
 *  Mirrors the strategy row with submission_type in place of pathway. */
export interface AdminClinicalRun {
  id: number;
  status: string;
  agent_kind: string;
  country: string | null;
  submission_type: string | null;
  product_id: number | null;
  product_name: string | null;
  company_name: string | null;
  dispatched_by: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  agent_retry_count: number;
  age_minutes: number | null;
  /** A running run whose heartbeat went stale (10+ min — worker likely died). */
  stale: boolean;
}

export async function getAdminClinicalRuns(
  kind: ClinicalAgentKind,
): Promise<AdminClinicalRun[]> {
  const res = await apiCall<{ runs: AdminClinicalRun[] }>(`/api/admin/${kind}-runs`, {
    method: 'GET',
  });
  return res.runs;
}

/** Force a queued/running clinical run to failed (atomic; clears a wedged run). */
export async function forceFailClinicalRun(
  kind: ClinicalAgentKind,
  runId: number,
): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/admin/${kind}-runs/${runId}/force-fail`, {
    method: 'POST',
  });
}

/** Retry a FAILED clinical run — re-queues it for a fresh full re-run and
 *  re-dispatches it (the worker rebuilds every input from the DB by run_id). */
export async function retryClinicalRun(
  kind: ClinicalAgentKind,
  runId: number,
): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/admin/${kind}-runs/${runId}/retry`, {
    method: 'POST',
  });
}

// ── MCP Observability ────────────────────────────────────────────────────────

export interface McpObservabilityAgentRow {
  agent: string;
  runs: number;
  avg_tool_calls: number;
  avg_duration_s: number;
  by_status: Record<string, number>;
}

export interface McpObservabilityConcurrencyInfo {
  overall_max: number;
  per_agent_max: Record<string, number>;
}

export interface McpObservabilityToolRow {
  tool: string;
  ok: number;
  failed: number;
  host: string | null;
}

export interface McpObservabilitySnapshot {
  agents: McpObservabilityAgentRow[];
  concurrency: McpObservabilityConcurrencyInfo;
  caps: Record<string, number>;
  statuses: Record<string, number>;
  fail_reasons: [string, number][];
  tool_health: McpObservabilityToolRow[];
  window_seconds: number | null;
}

/** Fleet-wide MCP observability snapshot derived from DB run history. */
export async function getMcpObservability(
  windowSeconds: number,
): Promise<McpObservabilitySnapshot> {
  return apiCall<McpObservabilitySnapshot>(
    `/api/admin/mcp-observability?window_seconds=${windowSeconds}`,
    { method: 'GET' },
  );
}

// ── Corpus Observability ─────────────────────────────────────────────────────

export type CorpusDatasetStatus = 'current' | 'behind' | 'overdue' | 'failed' | 'unknown';

/** The most recent completed ingest run, or null when none is recorded. */
export interface CorpusObservabilityRun {
  started_at: string;
  finished_at: string;
  failures: number;
  age_hours: number;
  /** No completed run inside the freshness window (26h). */
  overdue: boolean;
}

export interface CorpusObservabilityDataset {
  name: string;
  /** Last run's outcome for this dataset, e.g. "updated" / "unchanged" /
   *  "skipped-fresh" / "FAILED: ...". Null when the last run has no entry. */
  verdict: string | null;
  built_at: string | null;
  version: string | null;
  record_count: number | null;
  /** Our stamped source version vs the publisher's — "—" in the UI when unstamped. */
  source_version: string | null;
  publisher_version: string | null;
  fallback_version: string | null;
  age_hours: number | null;
  status: CorpusDatasetStatus;
  /** Edition this box has on local disk (sync ledger), null before first sync. */
  local_version: string | null;
  /** ISO timestamp of the most recent sync of this dataset to local disk. */
  local_synced_at: string | null;
}

export interface CorpusObservabilitySnapshot {
  available: boolean;
  unavailable_reason: string | null;
  generated_at: string;
  run: CorpusObservabilityRun | null;
  datasets: CorpusObservabilityDataset[];
}

/** Corpus ingest observability: last run health + per-dataset freshness. */
export async function getCorpusObservability(): Promise<CorpusObservabilitySnapshot> {
  return apiCall<CorpusObservabilitySnapshot>('/api/admin/corpus-observability', {
    method: 'GET',
  });
}

// --- Vault uploads → promote into a product's supporting docs (data collection) ---

export interface AdminVaultUploadRow {
  upload_id: number;
  ref: string;
  customer_id: number;
  company: string;
  filename: string;
  doc_type: string;                 // the leaf folder name
  /** The folder NODE this upload lives in (null = loose at the vault root). */
  folder_id: number | null;
  /** Full path, e.g. "StethoMe AI / TF.18 / DRAFT" (null = loose). */
  folder_path: string | null;
  size_bytes: number;
  uploaded_at: string | null;
  /** The upload's domain: device|ivd|drug|biologic|other (or null). */
  domain: string | null;
  /** The product this upload's folder is linked to — pre-targets promote. */
  folder_product_id: number | null;
  folder_product: string | null;
  /** false for types persist can't convert (only PDF/DOCX/XLSX/CSV promote). */
  promotable: boolean;
  promoted: boolean;
  promoted_to: string | null; // the product it last went to, e.g. "Widget Monitor · #234"
  promoted_at: string | null;
  conversion: string | null; // 'ready' | 'converting' | 'failed' | null (not promoted)
}

/** A company folder linked to a live product, read from the folder TREE (not files) so a linked
 *  CONTAINER folder (only subfolders, no direct files) can still show its product badge. */
export interface AdminVaultFolderLink {
  folder_id: number;
  path: string; // full path, e.g. "StethoMe AI / TF.18"
  product_id: number;
  product_name: string;
}

export interface AdminVaultUploadsPage {
  items: AdminVaultUploadRow[];
  total: number;
  offset: number;
  limit: number;
  /** Linked folders for the whole company — populated on the first page (offset 0) only. */
  folder_links?: AdminVaultFolderLink[];
}

/** One page of training-track vault uploads. `customerId` narrows to one company (used when a
 *  company is expanded in the panel). */
export async function getAdminVaultUploads(
  offset = 0,
  limit = 25,
  search?: string,
  promoted?: string,
  customerId?: number,
  domain?: string,
): Promise<AdminVaultUploadsPage> {
  const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
  if (search && search.trim()) params.set('search', search.trim());
  if (promoted) params.set('promoted', promoted);
  if (customerId != null) params.set('customer_id', String(customerId));
  if (domain) params.set('domain', domain);
  return apiCall<AdminVaultUploadsPage>(`/api/admin/vault-uploads?${params.toString()}`, {
    method: 'GET',
  });
}

export interface AdminVaultCompanyRow {
  customer_id: number;
  company: string;
  upload_count: number;
  promoted_count: number;
  /** AI-training consent track. The panel lists ALL companies (#47), so a badge flags Non-AI ones. */
  ai_training_track: 'non_training' | 'training';
}

export interface AdminVaultCompaniesPage {
  items: AdminVaultCompanyRow[];
  total: number;
  offset: number;
  limit: number;
}

export interface AdminPromoteFolderResult {
  promoted: number;
  skipped: number;
}

/** Promote every promotable file in a company's folder NODE (by id) into a product (bulk). */
export async function promoteFolder(
  customerId: number,
  folderId: number,
  productId: number,
): Promise<AdminPromoteFolderResult> {
  return apiCall<AdminPromoteFolderResult>('/api/admin/vault-uploads/promote-folder', {
    method: 'POST',
    json: { customer_id: customerId, folder_id: folderId, product_id: productId },
  });
}

/** One page of training-track companies that have vault uploads (with per-company counts). */
export async function getAdminVaultCompanies(
  offset = 0,
  limit = 25,
  search?: string,
  domain?: string,
  promoted?: string,
): Promise<AdminVaultCompaniesPage> {
  const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
  if (search && search.trim()) params.set('search', search.trim());
  if (domain) params.set('domain', domain);
  if (promoted) params.set('promoted', promoted);
  return apiCall<AdminVaultCompaniesPage>(`/api/admin/vault-companies?${params.toString()}`, {
    method: 'GET',
  });
}

export interface AdminCompanyProduct {
  id: number;
  name: string;
}

/** The products a company owns — the promote target picker. */
export async function getCompanyProducts(customerId: number): Promise<AdminCompanyProduct[]> {
  const res = await apiCall<{ products: AdminCompanyProduct[] }>(
    `/api/admin/companies/${customerId}/products`,
    { method: 'GET' },
  );
  return res.products;
}

export interface AdminVaultPromoteResult {
  doc_id: number;
  product_id: number;
  product_name: string;
  filename: string;
}

/** Promote a training-track vault upload into a product's supporting docs. */
export async function promoteVaultUpload(
  uploadId: number,
  productId: number,
): Promise<AdminVaultPromoteResult> {
  return apiCall<AdminVaultPromoteResult>(`/api/admin/vault-uploads/${uploadId}/promote`, {
    method: 'POST',
    json: { product_id: productId },
  });
}

/** Undo a promotion — remove the promoted supporting doc from its product. */
export async function unpromoteVaultUpload(uploadId: number): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/admin/vault-uploads/${uploadId}/unpromote`, {
    method: 'POST',
  });
}

/* ── Billing ─────────────────────────────────────────────────────────────── */

export type CollectionMethod = 'charge_automatically' | 'send_invoice';

export interface CompanyBilling {
  customer_id: number;
  status: string | null;
  collection_method: CollectionMethod | null;
  /** Display only, explicitly non-authoritative — the rate that bills lives in
   * the Stripe Price. */
  rate_label: string | null;
  has_payment_method: boolean;
  current_period_start: string | null;
  current_period_end: string | null;
  stripe_subscription_id: string | null;
  override_until: string | null;
}

export interface ProvisionBillingArgs {
  /** Free text by design: negotiated per contract, not chosen from a list. */
  rate_per_million: string;
  collection_method: CollectionMethod;
  tax_id: string | null;
  /** eu_vat, gb_vat, ch_vat … — the type is not inferable from the value. */
  tax_id_type: string | null;
}

/** Billing state for one company. ``null`` means not provisioned, which is the
 * ordinary state before an admin sets it up — not an error. */
export async function fetchCompanyBilling(customerId: number): Promise<CompanyBilling | null> {
  try {
    return await apiCall<CompanyBilling>(`/api/admin/companies/${customerId}/billing`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export async function provisionCompanyBilling(
  customerId: number,
  args: ProvisionBillingArgs,
): Promise<CompanyBilling> {
  return apiCall<CompanyBilling>(`/api/admin/companies/${customerId}/billing`, {
    method: 'POST',
    json: args,
  });
}

/* ── Billing health ──────────────────────────────────────────────────────── */

export interface BillingStatusHealth {
  count: number;
  tokens: number;
  oldest: string | null;
  /** Days until Stripe stops accepting the OLDEST row. null where no clock
   * applies — expired is already past it, and rejected/stranded wait on a
   * decision rather than on time. */
  days_left: number | null;
}

export interface StuckWebhookEvents {
  count: number;
  oldest: string | null;
  /** false once the oldest is past Stripe's 30-day retention, after which it
   * cannot be replayed at all. */
  replayable: boolean | null;
}

export interface FailingWebhookEvents {
  count: number;
  /** Highest attempt count among them — one event failing nine times is a very
   * different problem from nine events failing once. */
  max_attempts: number;
  oldest: string | null;
  /** Exception class name from the worst offender. Never a message — those can
   * quote customer data. */
  last_error: string | null;
}

export interface BillingHealth {
  unbilled: BillingStatusHealth;
  expired: BillingStatusHealth;
  rejected: BillingStatusHealth;
  stranded: BillingStatusHealth;
  stuck_events: StuckWebhookEvents;
  failing_events: FailingWebhookEvents;
  total_at_risk: number;
  total_tokens_at_risk: number;
}

/** Recorded usage that has not become revenue. */
export async function getBillingHealth(): Promise<BillingHealth> {
  return apiCall<BillingHealth>('/api/admin/billing-health');
}

/** Grant or clear the time-boxed dispatch exemption. ``until: null`` clears. */
export async function setBillingOverride(
  customerId: number,
  until: string | null,
): Promise<CompanyBilling> {
  return apiCall<CompanyBilling>(
    `/api/admin/companies/${customerId}/billing/override`,
    { method: 'POST', json: { until } },
  );
}

/** The settings PUT's "this would strand existing products" refusal, or null. */
export function productsOverLimitFromConflict(
  err: unknown,
): { affected: number; message: string } | null {
  if (!(err instanceof ApiConflictError)) return null;
  const raw = err.detail as { detail?: unknown } | undefined;
  const d = raw && typeof raw === 'object' && 'detail' in raw ? raw.detail : raw;
  if (!d || typeof d !== 'object') return null;
  const body = d as Record<string, unknown>;
  if (body.reason !== 'products_over_limit') return null;
  return {
    affected: typeof body.affected_products === 'number' ? body.affected_products : 0,
    message: typeof body.message === 'string' ? body.message : 'Some products already hold more than this.',
  };
}
